import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import Icon from '../AppIcon';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/user-login');
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setIsLoggingOut(false);
      setIsMenuOpen(false);
      setIsMobileMenuOpen(false);
    }
  };

  const handleProfileClick = () => {
    navigate('/subscription-management');
    setIsMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  if (!user) return null;

  const getInitials = () => {
    if (profile?.full_name) {
      return profile?.full_name?.split(' ')?.map(name => name?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase();
  };

  const navigationItems = [
    { path: '/user-dashboard', label: 'Dashboard', icon: 'Home' },
    { path: '/ai-agent-workflow-hub', label: 'AI Workflow', icon: 'Sparkles' },
    { path: '/book-project-details', label: 'My Projects', icon: 'FolderOpen' },
    { path: '/subscription-management', label: 'Subscription', icon: 'CreditCard' },
    { path: '/help-center', label: 'Help Center', icon: 'HelpCircle' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4">
        <div className="flex items-center space-x-4">
          {/* Hamburger Menu Button - Mobile Only */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-accent transition-colors"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>

          <button
            onClick={() => navigate('/user-dashboard')}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
          >
            <Icon name="BookOpen" size={24} color="var(--color-primary)" />
            <span className="font-bold text-xl text-primary">AuthorPreneur</span>
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-accent transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                {getInitials()}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium">{profile?.full_name || 'User'}</p>
                <p className="text-xs text-muted-foreground capitalize">{profile?.subscription_tier || 'Free'}</p>
              </div>
              <Icon name={isMenuOpen ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </button>

            {isMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-card border rounded-lg shadow-lg z-50">
                  <div className="p-4 border-b">
                    <p className="text-sm font-medium truncate">{profile?.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={handleProfileClick}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors flex items-center space-x-2"
                    >
                      <Icon name="Settings" size={16} />
                      <span>Profile & Settings</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/user-dashboard');
                        setIsMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors flex items-center space-x-2"
                    >
                      <Icon name="Home" size={16} />
                      <span>Dashboard</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/ai-agent-workflow-hub');
                        setIsMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-accent transition-colors flex items-center space-x-2"
                    >
                      <Icon name="Sparkles" size={16} />
                      <span>AI Workflow</span>
                    </button>
                  </div>
                  <div className="border-t py-2">
                    <button
                      onClick={handleSignOut}
                      disabled={isLoggingOut}
                      className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 transition-colors flex items-center space-x-2"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>{isLoggingOut ? 'Signing out...' : 'Sign Out'}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Mobile Menu */}
          <div className="fixed left-0 top-16 bottom-0 w-64 bg-card border-r shadow-lg z-50 md:hidden transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* User Info Section */}
              <div className="p-4 border-b">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    {getInitials()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{profile?.full_name || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
              </div>

              {/* Navigation Items */}
              <nav className="flex-1 overflow-y-auto py-4">
                {navigationItems?.map((item) => {
                  const isActive = location?.pathname === item?.path;
                  return (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className={`w-full px-4 py-3 text-left flex items-center space-x-3 transition-colors ${
                        isActive
                          ? 'bg-primary/10 text-primary border-l-4 border-primary' :'hover:bg-accent text-foreground'
                      }`}
                    >
                      <Icon name={item?.icon} size={20} />
                      <span className="text-sm font-medium">{item?.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Sign Out Button */}
              <div className="p-4 border-t">
                <button
                  onClick={handleSignOut}
                  disabled={isLoggingOut}
                  className="w-full px-4 py-3 text-left text-sm text-error hover:bg-error/10 transition-colors flex items-center space-x-3 rounded-lg"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="font-medium">{isLoggingOut ? 'Signing out...' : 'Sign Out'}</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;